import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { PapiDeviation } from '@wix/da-papi-types';
import { SellableAs } from '@wix/da-shared-react/pkg/types/deviation';
import { getSellableAs } from '@wix/da-shared-react/pkg/types/deviation/getters';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import FixedPriceDollarInput from '../../../modals/seller/form/FixedPriceDollarInput';
import FixedPriceSectionToggle from '../../../modals/seller/form/FixedPriceSectionToggle';
import OfferSectionToggle from '../../../modals/seller/form/OfferSectionToggle';
import OfferDollarInput from '../../../modals/seller/form/OfferDollarInput';
import OfferDurationSelectField from '../../../modals/seller/form/OfferDurationSelectField';
import SourceFilesInput from '../../../modals/seller/form/SourceFilesInput';
import ButtonFieldLayout from '../../../layouts/common/ButtonFieldLayout';
import AdoptableSettingsModal, {
  SettingsFormFields,
} from '../AdoptableSettingsModal';
import { useCreateForm } from '../../../modals/seller/form/hooks';
import { adoptableSubmitAdapter } from '../../../modals/seller/redux/sellerCreateAdoptable/adapters';
import { FormField } from '../../../modals/seller/redux/types';
import { SubmitFormContext } from '../../../context';
import InlineExclusiveForm from '../InlineExclusiveForm';
import s from './CreateExclusiveForm.scss';

interface Props {
  deviation: PapiDeviation;
}

const CreateExclusiveForm: React.FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const openAdvancedSettings = () => setShowAdvancedSettings(true);
  const closeAdvancedSettings = () => setShowAdvancedSettings(false);

  const { initResponse } = useContext(SubmitFormContext) || {};
  const formContext = useCreateForm(
    [deviation as PapiDeviation],
    false,
    {
      [deviation.deviationId]: initResponse?.adoptable?.assets || [],
    }
  );
  const sellableAs = getSellableAs(deviation);
  const isRelisting = sellableAs.includes(SellableAs.AdoptableResale);

  const {
    [FormField.SellAsOffer]: sellAsOffer,
    ...advancedSettingsInitialValues
  } = useWatch({
    control: formContext.control,
    name: [
      FormField.SellAsOffer,
      FormField.Category,
      FormField.EarlyAccess,
      FormField.AllowResale,
      FormField.OfferDelayedStart,
    ],
  });

  useEffect(() => {
    formContext.register(FormField.Category);
    formContext.register(FormField.EarlyAccess);
    formContext.register(FormField.AllowResale);
    formContext.register(FormField.OfferDelayedStart);
  }, [formContext]);

  const setAdvancedSettingsValues = useCallback(
    data => {
      Object.entries(data).forEach(([field, value]) => {
        formContext.setValue(field, value, { shouldDirty: true });
      });
    },
    [formContext]
  );

  // Populate the initial assets
  if (
    deviation.extended &&
    (initResponse?.adoptable?.assets || []).length &&
    initResponse?.adoptable?.creator &&
    initResponse?.adoptable?.owner
  ) {
    deviation.extended.adoptable = {
      ...(deviation.extended.adoptable || {}),
      creator: initResponse?.adoptable?.creator,
      owner: initResponse?.adoptable?.owner,
      assets: initResponse?.adoptable?.assets,
    };
  }

  return (
    <InlineExclusiveForm
      formName="createExclusive"
      formContext={formContext}
      submitAdapter={data => ({
        createExclusive: adoptableSubmitAdapter(
          deviation,
          data['createExclusive']
        ),
      })}
    >
      <div className={s['root']}>
        <div className={s['checkbox-input-wrapper']}>
          <FixedPriceSectionToggle />

          <FixedPriceDollarInput
            className={s['purchase-price']}
            isResale={isRelisting}
          />
        </div>

        <div className={s['checkbox-input-wrapper']}>
          <OfferSectionToggle />
          <div className={s['offer-controls']}>
            <OfferDollarInput className={s['offer-price']} />
            <OfferDurationSelectField className={s['offer-duration']} />
          </div>
        </div>
        <SourceFilesInput deviation={deviation} />

        <ButtonFieldLayout
          label={t('deviationSubmit.form.sellExclusive.advancedSettings.label')}
        >
          <TextButton
            variant="main"
            size="small"
            onClick={openAdvancedSettings}
          >
            {t('common.edit')}
          </TextButton>
        </ButtonFieldLayout>

        {showAdvancedSettings && (
          <AdoptableSettingsModal
            acceptingOffers={!!sellAsOffer}
            initialValues={advancedSettingsInitialValues as SettingsFormFields}
            onRequestClose={closeAdvancedSettings}
            onSave={setAdvancedSettingsValues}
            isOpen
          />
        )}
      </div>
    </InlineExclusiveForm>
  );
};
CreateExclusiveForm.displayName = 'CreateExclusiveForm';

export default CreateExclusiveForm;
